/**
 * Project detail page (`/projects/$projectId`).
 *
 * Top: breadcrumb + project header (number, title, status, days-in-state,
 * overdue indicator).
 *
 * Below: tabs that swap the body:
 *   - General particulars: stat tiles + DFA/contract flow buttons + project metadata
 *   - Departments & addresses: roles + contacts management
 *   - Schedule of store / Batches / Billing: procurement and fulfilment workflow
 *   - Audit: placeholder (Pass 17)
 */
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, CheckCircle2, Edit3, FileText, Lock, MessageSquare, Pencil, RotateCcw, X } from 'lucide-react';
import { formatBpsAsPercent, formatCurrency, formatDate, formatDateLong, formatUppercaseBusinessText, todayPktIsoDate, computeProjectProgressBuckets, TermsOfDelivery, parsePercentInputToBps, parseRupeeInputToPaisa } from '@procurement/shared';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Input, Textarea } from '@/components/ui/form-field';
import { Modal } from '@/components/ui/modal';
import { PageHeader } from '@/components/ui/page-header';
import { ProjectProgressBar } from '@/components/project-progress-bar';
import { Spinner } from '@/components/ui/spinner';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusBadge } from '@/components/ui/status-badge';
import { Select } from '@/components/ui/select';
import { ProjectNumber } from '@/components/ui/project-number';
import { Tabs, type TabDef } from '@/components/ui/tabs';
import { LogCommunicationModal } from '@/components/attachments/log-communication-modal';
import { useCurrentUser } from '@/lib/auth';
import { isLockedForUser } from '@/lib/closure-lock';
import { usePageTitle } from '@/lib/use-page-title';
import { useDiscardDraft, useDraftsList, useSaveDraft } from '@/lib/draft-queries';
import {
  type Project,
  type ProjectCurrencyCode,
  type ProjectInspectionLocation,
  type ProjectTermsOfDelivery,
  useCloseProject,
  useClosureReadiness,
  useProject,
  useReopenProject,
  useUpdateProject,
} from '@/lib/project-queries';
import { ProjectDepartmentsTab } from './project-departments-tab';
import { ProjectPartsTab } from './project-parts-tab';
import { ProjectBatchesTab } from './project-batches-tab';
import { computeScheduleAmountComparison } from '@/lib/project-financials';
import { ProjectFinancialsTab } from './project-financials-tab';
import { useProjectItems, useSuppliersList } from '@/lib/pipeline-queries';
import { useIncotermsListAll } from '@/lib/masters-queries';
import { useClientsList, useDepartmentsList } from '@/lib/project-queries';
import { ClosureBanner } from '@/components/closure-banner';
import { NdcSuggestionBanner } from '@/components/ndc-suggestion-banner';
import { CloseProjectSuggestionBanner } from '@/components/close-project-suggestion-banner';
import { ProjectBillingTab } from './project-billing-tab';
import { ProjectAuditTab } from './project-audit-tab';
import { ProjectDocumentsTab } from './project-documents-tab';

type TabId = 'overview' | 'parts' | 'batches' | 'financials' | 'billing' | 'departments' | 'documents' | 'audit';

const TABS: TabDef<TabId>[] = [
  { id: 'overview', label: 'General particulars' },
  { id: 'departments', label: 'Departments & addresses' },
  { id: 'parts', label: 'Schedule of store' },
  { id: 'batches', label: 'Batches' },
  { id: 'financials', label: 'Financials' },
  { id: 'billing', label: 'Billing' },
  { id: 'documents', label: 'Documents' },
  { id: 'audit', label: 'Audit' },
];

export function ProjectDetailPage() {
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const me = useCurrentUser();
  const project = useProject(projectId);
  const [tab, setTab] = useState<TabId>('overview');
  // E26 — project header "Log communication" button
  const [logCommOpen, setLogCommOpen] = useState(false);

  // Title uses the project number once loaded. Pass null pre-load so we
  // don't flash a "Loading… · TenderLens" tab.
  usePageTitle(project.data ? `${project.data.projectNumber} — ${project.data.projectTitle}` : null);

  // Patch P4 (fixed) — master lists for the project-header "Log
  // communication" CTA. Sub-anchor entities (parts/batches/BGs/inspections)
  // are loaded by the modal itself via `useAnchorEntities`, so they don't
  // need to be loaded here. Master lists still need to come from the
  // parent because the modal accepts them as opaque option arrays.
  //
  // Hooks MUST sit above the early returns below so they run on every
  // render (the prior placement after `project.isLoading` violated the
  // Rules of Hooks).
  const clientsForAnchors = useClientsList();
  const suppliersForAnchors = useSuppliersList();
  const departmentsForAnchors = useDepartmentsList();

  if (project.isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }
  if (project.error || !project.data) {
    return (
      <Card>
        <EmptyState
          icon={<FileText className="h-6 w-6" />}
          title="Project not found"
          body="It may have been deleted, or you don't have access. Return to the projects list."
          action={
            <Link to="/projects">
              <Button variant="secondary">Back to projects</Button>
            </Link>
          }
        />
      </Card>
    );
  }

  const p = project.data;
  const isAdmin = me.data?.user.role === 'ADMIN';
  const locked = isLockedForUser(p, isAdmin);

  const masterOptions = {
    clients: (clientsForAnchors.data ?? []).map((c) => ({ id: c.id, label: c.name })),
    suppliers: (suppliersForAnchors.data ?? []).map((s) => ({ id: s.id, label: s.name })),
    departments: (departmentsForAnchors.data ?? []).map((d) => ({ id: d.id, label: d.name })),
  };

  return (
    <>
      <div className="mb-2">
        <Link
          to="/projects"
          className="inline-flex items-center gap-1 text-xs text-secondary hover:text-primary"
        >
          <ArrowLeft className="h-3 w-3" /> All projects
        </Link>
      </div>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <ProjectNumber number={p.projectNumber} />
            <span>{p.projectTitle}</span>
          </span>
        }
        subtitle={
          <span className="flex items-center gap-2">
            <StatusBadge kind="lifecycle" value={p.lifecycleState} />
            {p.isOverdue ? <Badge tone="danger">Overdue</Badge> : null}
            <span className="text-xs text-secondary">
              · {p.daysInLifecycleState} day{p.daysInLifecycleState === 1 ? '' : 's'} in state
            </span>
          </span>
        }
        actions={
          // Spec §9 / §13 row 23 — Reopen edits a closed project; Admin only.
          // Spec §13 row 22 — Setting Project Closed is open to both roles.
          // Patch E26 — "Log communication" alongside Close/Reopen. Always
          // available regardless of lifecycle state (a closed project can
          // still accumulate post-handover correspondence). Project-level
          // anchor by default; sub-anchor selection still possible inside
          // the modal but its entity lists are empty here — full picker
          // lives in the Documents tab.
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="compact"
              onClick={() => setLogCommOpen(true)}
              leftIcon={<MessageSquare className="h-3.5 w-3.5" strokeWidth={1.5} />}
            >
              Log communication
            </Button>
            {isAdmin && p.lifecycleState === 'CLOSED' ? (
              <ReopenButton projectId={p.id} rowVersion={p.rowVersion} />
            ) : p.lifecycleState !== 'CLOSED' && p.partsWorkflowState === 'FINALISED' ? (
              <CloseButton projectId={p.id} rowVersion={p.rowVersion} />
            ) : null}
          </div>
        }
      />

      <LogCommunicationModal
        open={logCommOpen}
        onClose={() => setLogCommOpen(false)}
        projectId={p.id}
        defaultAnchor={{ projectId: p.id }}
        clients={masterOptions.clients}
        suppliers={masterOptions.suppliers}
        departments={masterOptions.departments}
      />

      <ClosureBanner project={p} />
      <NdcSuggestionBanner project={p} />
      <CloseProjectSuggestionBanner project={p} />

      <Tabs<TabId> tabs={TABS} activeId={tab} onChange={setTab} className="mb-6" />

      {tab === 'overview' ? <OverviewTab project={p} /> : null}
      {tab === 'parts' ? <ProjectPartsTab project={p} /> : null}
      {tab === 'batches' ? <ProjectBatchesTab project={p} /> : null}
      {tab === 'financials' ? <ProjectFinancialsTab project={p} /> : null}
      {tab === 'billing' ? <ProjectBillingTab project={p} /> : null}
      {tab === 'departments' ? <ProjectDepartmentsTab project={p} /> : null}
      {tab === 'documents' ? <ProjectDocumentsTab projectId={p.id} canEdit={!locked} /> : null}
      {tab === 'audit' ? <ProjectAuditTab projectId={p.id} /> : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// General particulars tab
// ---------------------------------------------------------------------------

function OverviewTab({ project }: { project: Project }) {
  return (
    <>
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatTile
          label="Parts status"
          value={
            project.partsWorkflowState === 'FINALISED'
              ? 'Finalised'
              : project.partsWorkflowState === 'PENDING_APPROVAL'
                ? 'Pending'
                : project.partsWorkflowState === 'ENTERING'
                  ? 'In progress'
                  : 'Not started'
          }
          tone={project.partsWorkflowState === 'FINALISED' ? 'success' : 'neutral'}
        />
        <StatTile
          label="Contract value"
          value={
            project.contractValuePaisa > 0
              ? formatCurrency(project.contractValuePaisa, { compact: true })
              : '—'
          }
          hint="Sum of qty × client unit price"
        />
        <StatTile
          label="Supplier cost"
          value={project.costPaisa > 0 ? formatCurrency(project.costPaisa, { compact: true }) : '—'}
        />
        <StatTile
          label="Margin"
          value={
            project.contractValuePaisa === 0 && project.costPaisa === 0
              ? '—'
              : formatCurrency(project.marginPaisa, { compact: true })
          }
          tone={
            project.contractValuePaisa === 0 && project.costPaisa === 0
              ? 'neutral'
              : project.marginPaisa > 0
                ? 'success'
                : project.marginPaisa < 0
                  ? 'danger'
                  : 'neutral'
          }
          hint={
            project.marginPaisa < 0
              ? 'Cost exceeds contract value'
              : project.contractValuePaisa > 0
                ? 'Contract value − cost'
                : undefined
          }
        />
        <StatTile
          label="Warranty end"
          value={project.warrantyEndDate ? formatDate(project.warrantyEndDate) : '—'}
          hint={project.warrantyEndDate ? 'Triggers BG release' : 'Pending delivery'}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ProjectIdentityCard project={project} />
        <DfaFlowCard project={project} />
        <ProjectMetadataCard project={project} />
        <ContractIndentCard project={project} />
        <CommercialTermsCard project={project} />
        <CurrencyPrincipalCard project={project} />
      </div>

      {/* E20: pipeline-progress card — single answer to "where is this
       * project sitting across its 18-stage pipeline?". Sits below the
       * money-tile grid so the contract-value tiles stay above the fold;
       * sits above the DFA/metadata grid so it has visual weight. */}
      <div className="mt-6">
        <ProjectProgressSection projectId={project.id} />
      </div>
    </>
  );
}


function ProjectIdentityCard({ project }: { project: Project }) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const locked = isLockedForUser(project, isAdmin);
  return (
    <Card title="Project identity">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <EditableRequiredTextField
          project={project}
          locked={locked}
          label="Project title"
          field="projectTitle"
          value={project.projectTitle}
          uppercaseDisplay
          maxWidth="max-w-[320px]"
        />
        <EditableRequiredTextField
          project={project}
          locked={locked}
          label="Project number"
          field="projectNumber"
          value={project.projectNumber}
          uppercaseDisplay
        />
        <EditableClientField project={project} locked={locked} />
      </dl>
    </Card>
  );
}

function ProjectProgressSection({ projectId }: { projectId: string }) {
  const items = useProjectItems(projectId);
  if (items.isLoading) {
    return (
      <Card title="Pipeline progress">
        <div className="flex items-center justify-center py-3">
          <Spinner className="h-4 w-4 text-secondary" />
        </div>
      </Card>
    );
  }
  // Only PART rows feed the bucket distribution; SUB_PARTs don't carry a
  // pipeline stage in the same semantic sense (their progression is folded
  // into the parent PART's currentStageNumber by derived-item-state).
  const parts = (items.data ?? []).filter(
    (i) => i.itemType === 'PART' && i.deletedAt === null,
  );
  const buckets = computeProjectProgressBuckets(parts);
  return (
    <Card title="Pipeline progress">
      <ProjectProgressBar buckets={buckets} variant="full" />
    </Card>
  );
}

function DfaFlowCard({ project }: { project: Project }) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const locked = isLockedForUser(project, isAdmin);
  const update = useUpdateProject(project.id);
  const [editing, setEditing] = useState<'received' | 'submit' | 'approve' | null>(null);
  const [date, setDate] = useState('');

  const startEdit = (which: 'received' | 'submit' | 'approve'): void => {
    const prefill =
      which === 'received'
        ? project.dfaReceivedDate
        : which === 'submit'
          ? (project.dfaSubmittedDate ?? '')
          : (project.contractSignDate ?? '');
    setDate(prefill || todayPktIsoDate());
    setEditing(which);
  };

  const save = (): void => {
    if (!editing) return;
    const patch =
      editing === 'received'
        ? { dfaReceivedDate: date, rowVersion: project.rowVersion }
        : editing === 'submit'
          ? { dfaSubmittedDate: date, rowVersion: project.rowVersion }
          : { contractSignDate: date, rowVersion: project.rowVersion };
    update.mutate(patch, {
      onSuccess: () => {
        setEditing(null);
        setDate('');
      },
    });
  };

  return (
    <Card title="Contract flow">
      {update.error ? (
        <Alert tone="danger" className="mb-3">
          {update.error.message}
        </Alert>
      ) : null}
      <ul className="space-y-3 text-sm">
        <FlowRow
          label="DFA received"
          date={project.dfaReceivedDate}
          icon={<CheckCircle2 className="h-4 w-4" />}
          done
          action={
            !locked && editing !== 'received' ? (
              <Button
                size="compact"
                variant="tertiary"
                leftIcon={<Edit3 className="h-3 w-3" />}
                onClick={() => startEdit('received')}
              >
                Edit
              </Button>
            ) : null
          }
          editing={editing === 'received'}
          editingValue={date}
          onEditingChange={setDate}
          onSave={save}
          onCancel={() => setEditing(null)}
          saving={update.isPending && editing === 'received'}
        />
        <FlowRow
          label="DFA submitted"
          date={project.dfaSubmittedDate}
          icon={<CheckCircle2 className="h-4 w-4" />}
          done={project.dfaSubmittedDate !== null}
          action={
            !locked && editing !== 'submit' ? (
              <Button
                size="compact"
                variant="tertiary"
                leftIcon={<Edit3 className="h-3 w-3" />}
                onClick={() => startEdit('submit')}
              >
                {project.dfaSubmittedDate ? 'Edit' : 'Record'}
              </Button>
            ) : null
          }
          editing={editing === 'submit'}
          editingValue={date}
          onEditingChange={setDate}
          onSave={save}
          onCancel={() => setEditing(null)}
          saving={update.isPending && editing === 'submit'}
        />
        <FlowRow
          label="Contract signed"
          date={project.contractSignDate}
          icon={<CheckCircle2 className="h-4 w-4" />}
          done={project.contractSignDate !== null}
          action={
            !locked && editing !== 'approve' ? (
              <Button
                size="compact"
                variant="tertiary"
                leftIcon={<Edit3 className="h-3 w-3" />}
                onClick={() => startEdit('approve')}
              >
                {project.contractSignDate ? 'Edit' : 'Record'}
              </Button>
            ) : null
          }
          editing={editing === 'approve'}
          editingValue={date}
          onEditingChange={setDate}
          onSave={save}
          onCancel={() => setEditing(null)}
          saving={update.isPending && editing === 'approve'}
        />
      </ul>
    </Card>
  );
}

interface FlowRowProps {
  label: string;
  date: string | null;
  icon: React.ReactNode;
  done?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  action?: React.ReactNode;
  editing?: boolean;
  editingValue?: string;
  onEditingChange?: (value: string) => void;
  onSave?: () => void;
  onCancel?: () => void;
  saving?: boolean;
}

function FlowRow({
  label,
  date,
  icon,
  done,
  disabled,
  disabledReason,
  action,
  editing,
  editingValue,
  onEditingChange,
  onSave,
  onCancel,
  saving,
}: FlowRowProps) {
  return (
    <li className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2">
        <span
          className={done ? 'text-status-success' : disabled ? 'text-tertiary' : 'text-secondary'}
        >
          {disabled ? <Lock className="h-4 w-4" /> : icon}
        </span>
        <div className="min-w-0">
          <div className="font-medium text-primary">{label}</div>
          {disabled ? (
            <div className="text-xs text-tertiary">🔒 {disabledReason}</div>
          ) : editing ? (
            <div className="mt-1 flex items-center gap-2">
              <DmyDateInput
                value={editingValue ?? ''}
                maxDate={todayPktIsoDate()}
                onChange={(value) => onEditingChange?.(value)}
                showValidationMessage={false}
                className="h-8 text-sm"
              />
              <Button size="compact" variant="primary" onClick={onSave} loading={saving}>
                Save
              </Button>
              <Button size="compact" variant="tertiary" onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="text-xs text-secondary tabular-nums">{formatDate(date)}</div>
          )}
        </div>
      </div>
      {action}
    </li>
  );
}



function ContractIndentCard({ project }: { project: Project }) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const locked = isLockedForUser(project, isAdmin);
  return (
    <Card title="Contract / indent">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <EditableTextField
          project={project}
          locked={locked}
          label="Origin"
          field="origin"
          value={project.origin}
          uppercaseDisplay
        />
        <EditableTextField
          project={project}
          locked={locked}
          label="Indent number"
          field="indentNumber"
          value={project.indentNumber}
          uppercaseDisplay
        />
        <EditableDateField
          project={project}
          locked={locked}
          label="Indent date"
          field="indentDate"
          value={project.indentDate}
        />
        <EditableTextField
          project={project}
          locked={locked}
          label="Maker / brand"
          field="makerNameBrand"
          value={project.makerNameBrand}
          uppercaseDisplay
        />
        <TermsOfDeliveryField project={project} locked={locked} />
      </dl>
    </Card>
  );
}

function CommercialTermsCard({ project }: { project: Project }) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const locked = isLockedForUser(project, isAdmin);
  const amountComparison = computeScheduleAmountComparison(
    project.totalStoreValuePaisa,
    project.contractValuePaisa,
  );
  return (
    <Card title="Commercial / BG terms">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <EditableMoneyField
          project={project}
          locked={locked}
          label="Original amount"
          field="totalStoreValuePaisa"
          value={project.totalStoreValuePaisa}
        />
        <Field
          label="Schedule of Stores total"
          value={project.contractValuePaisa > 0 ? formatCurrency(project.contractValuePaisa) : '—'}
        />
        <Field
          label="Amount discrepancy"
          value={formatScheduleAmountDiscrepancy(amountComparison)}
        />
        <EditablePercentField
          project={project}
          locked={locked}
          label="GST %"
          field="gstRateBps"
          value={project.gstRateBps}
        />
        <Field label="GST amount" value={formatOptionalCurrency(project.gstAmountPaisa)} />
        <Field
          label="Total contract value"
          value={formatOptionalCurrency(project.declaredTotalContractValuePaisa)}
        />
        <EditableMoneyField
          project={project}
          locked={locked}
          label="BG amount"
          field="requiredBgAmountPaisa"
          value={project.requiredBgAmountPaisa}
        />
        <EditableIntegerField
          project={project}
          locked={locked}
          label="BG submission"
          field="bgSubmissionDurationDays"
          value={project.bgSubmissionDurationDays}
          unit="days"
          min={0}
          max={3650}
          nullable
        />
        <EditableIntegerField
          project={project}
          locked={locked}
          label="BG validity"
          field="bgValidityDurationMonths"
          value={project.bgValidityDurationMonths}
          unit="months"
          min={1}
          max={240}
          nullable
        />
        <EditablePaymentSplitField project={project} locked={locked} />
        <EditableMoneyField
          project={project}
          locked={locked}
          label="Earnest money"
          field="earnestMoneyAmountPaisa"
          value={project.earnestMoneyAmountPaisa}
        />
        <EditableDateField
          project={project}
          locked={locked}
          label="Earnest submitted"
          field="earnestSubmittedDate"
          value={project.earnestSubmittedDate}
        />
        <EditableDateField
          project={project}
          locked={locked}
          label="Earnest released"
          field="earnestReleasedDate"
          value={project.earnestReleasedDate}
        />
        <EditableBooleanField
          project={project}
          locked={locked}
          label="Partial delivery"
          field="partialDeliveryAllowed"
          value={project.partialDeliveryAllowed}
        />
        <EditableBooleanField
          project={project}
          locked={locked}
          label="Partial payment"
          field="partialPaymentAllowed"
          value={project.partialPaymentAllowed}
        />
        <EditableTextField
          project={project}
          locked={locked}
          label="Partial remarks"
          field="partialDeliveryPaymentRemarks"
          value={project.partialDeliveryPaymentRemarks}
          uppercaseDisplay
        />
      </dl>
      {amountComparison.hasDiscrepancy ? (
        <Alert tone="warning" title="Amount discrepancy" className="mt-4">
          Original amount and Schedule of Stores total differ by{' '}
          <span className="font-semibold tabular-nums">
            {formatCurrency(amountComparison.absoluteDiscrepancyPaisa ?? 0)}
          </span>
          .
        </Alert>
      ) : null}
    </Card>
  );
}

function CurrencyPrincipalCard({ project }: { project: Project }) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const locked = isLockedForUser(project, isAdmin);
  const isForeign = project.projectCurrency !== 'PKR';
  return (
    <Card title="Currency / principal">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <ProjectCurrencyField project={project} locked={locked} />
        {project.projectCurrency === 'OTHER' ? (
          <EditableTextField
            project={project}
            locked={locked}
            label="Other currency"
            field="otherCurrencyCode"
            value={project.otherCurrencyCode}
            uppercaseDisplay
          />
        ) : null}
        {isForeign ? (
          <>
            <EditableTextField project={project} locked={locked} label="Principal name" field="principalName" value={project.principalName} uppercaseDisplay />
            <EditableTextField project={project} locked={locked} label="Contact person" field="principalContactPerson" value={project.principalContactPerson} uppercaseDisplay />
            <EditableTextField project={project} locked={locked} label="Principal address" field="principalAddress" value={project.principalAddress} uppercaseDisplay />
            <EditableTextField project={project} locked={locked} label="Principal email" field="principalEmail" value={project.principalEmail} />
            <EditableTextField project={project} locked={locked} label="Principal phone" field="principalPhone" value={project.principalPhone} />
            <EditableTextField project={project} locked={locked} label="Principal fax" field="principalFax" value={project.principalFax} />
            <EditableTextField project={project} locked={locked} label="Port of shipment" field="portOfShipment" value={project.portOfShipment} uppercaseDisplay />
            <EditableTextField project={project} locked={locked} label="Bank name" field="principalBankName" value={project.principalBankName} uppercaseDisplay />
            <EditableTextField project={project} locked={locked} label="Account number" field="principalBankAccountNumber" value={project.principalBankAccountNumber} />
            <EditableTextField project={project} locked={locked} label="IBAN" field="principalBankIban" value={project.principalBankIban} uppercaseDisplay />
            <EditableTextField project={project} locked={locked} label="Account title" field="principalBankAccountTitle" value={project.principalBankAccountTitle} uppercaseDisplay />
            <EditableTextField project={project} locked={locked} label="SWIFT" field="principalBankSwiftCode" value={project.principalBankSwiftCode} uppercaseDisplay />
            <EditableTextField project={project} locked={locked} label="Bank address" field="principalBankAddress" value={project.principalBankAddress} uppercaseDisplay />
            <EditableTextField project={project} locked={locked} label="Bank email" field="principalBankEmail" value={project.principalBankEmail} />
            <EditableTextField project={project} locked={locked} label="Bank phone" field="principalBankPhone" value={project.principalBankPhone} />
            <EditableTextField project={project} locked={locked} label="Bank fax" field="principalBankFax" value={project.principalBankFax} />
          </>
        ) : (
          <Field label="Foreign principal" value="NOT REQUIRED FOR PKR" />
        )}
      </dl>
    </Card>
  );
}

function formatOptionalCurrency(value: number | null): string {
  return value === null ? '—' : formatCurrency(value);
}

function formatScheduleAmountDiscrepancy(
  comparison: ReturnType<typeof computeScheduleAmountComparison>,
): string {
  if (!comparison.canCompare || comparison.discrepancyPaisa === null) return '—';
  if (!comparison.hasDiscrepancy) return 'No discrepancy';
  return formatCurrency(comparison.discrepancyPaisa);
}

function formatGeneralText(value: string | null | undefined): string {
  return formatUppercaseBusinessText(value);
}

function deriveExtendedDueDate(project: Project): string | null {
  if (!project.effectiveDeliveryTargetDate) return null;
  if (!Number.isFinite(project.gracePeriodDays) || project.gracePeriodDays <= 0) {
    return project.effectiveDeliveryTargetDate;
  }
  const date = new Date(`${project.effectiveDeliveryTargetDate}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return null;
  date.setUTCDate(date.getUTCDate() + project.gracePeriodDays);
  return date.toISOString().slice(0, 10);
}

function ProjectMetadataCard({ project }: { project: Project }) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  // Closed-project lock for Assistants; Admins keep edit access. Same gate
  // the API enforces, mirrored client-side so we don't show a pencil that
  // the API will reject.
  const locked = isLockedForUser(project, isAdmin);
  return (
    <Card title="Project details">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        {/* E23: each row below is now inline-editable. The hover-pencil reveals
         * an edit affordance; clicking it swaps the value for an input + save/
         * cancel buttons. Submits via the existing PATCH /api/projects/:id
         * (which already accepts every field listed here). Closed projects
         * hide edit affordances for Assistants. */}
        <Field label="Due date" value={formatDate(project.effectiveDeliveryTargetDate)} />
        <EditableDateField
          project={project}
          locked={locked}
          label="Date of issuance"
          field="dateOfIssuance"
          value={project.dateOfIssuance}
        />
        <EditableDateField
          project={project}
          locked={locked}
          label="Technical bid opening"
          field="technicalBidOpeningDate"
          value={project.technicalBidOpeningDate}
        />
        <EditableDateField
          project={project}
          locked={locked}
          label="Financial bid opening"
          field="financialBidOpeningDate"
          value={project.financialBidOpeningDate}
        />
        <EditableIntegerField
          project={project}
          locked={locked}
          label="Delivery duration"
          field="deliveryDurationMonths"
          value={project.deliveryDurationMonths}
          unit="months"
          min={1}
          max={240}
        />
        <EditableIntegerField
          project={project}
          locked={locked}
          label="Grace period"
          field="gracePeriodDays"
          value={project.gracePeriodDays}
          unit="days"
          min={0}
          max={365}
        />
        <Field label="Extended due date" value={formatDate(deriveExtendedDueDate(project))} />
        <EditableIntegerField
          project={project}
          locked={locked}
          label="Warranty"
          field="warrantyPeriodMonths"
          value={project.warrantyPeriodMonths}
          unit="months"
          min={0}
          max={120}
          nullable
        />
        <DefaultInspectionField project={project} />
        <EditableTextField
          project={project}
          locked={locked}
          label="Inspection authority"
          field="inspectionAuthority"
          value={project.inspectionAuthority}
          placeholder="e.g. DQA"
          uppercaseDisplay
        />
        <EditableBooleanField
          project={project}
          locked={locked}
          label="NDC required"
          field="ndcRequired"
          value={project.ndcRequired}
        />
        <Field label="NDC status" value={project.ndcIssuedDate ? 'ISSUED' : 'PENDING'} />
        <EditableDateField
          project={project}
          locked={locked}
          label="NDC issue date"
          field="ndcIssuedDate"
          value={project.ndcIssuedDate}
        />
        <EditableTextField
          project={project}
          locked={locked}
          label="NDC reference"
          field="ndcReferenceNumber"
          value={project.ndcReferenceNumber}
          uppercaseDisplay
        />
        <EditableTextField
          project={project}
          locked={locked}
          label="NDC remarks"
          field="ndcRemarks"
          value={project.ndcRemarks}
          uppercaseDisplay
        />
        <EditableTextField
          project={project}
          locked={locked}
          label="Consignee"
          field="consigneeLocation"
          value={project.consigneeLocation}
          placeholder="e.g. PAF Base Faisal, Karachi"
          uppercaseDisplay
        />
        <Field label="Created" value={formatDateLong(project.createdAt)} />
      </dl>
      <EditableRemarks project={project} locked={locked} />
    </Card>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">{label}</dt>
      <dd className="mt-0.5 text-sm text-primary tabular-nums">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline-edit primitives (E23)
// ---------------------------------------------------------------------------

/**
 * Common skeleton for any single-field edit on the project metadata card.
 *
 * View mode: <dt>label</dt> + <dd>value</dd>, with a Pencil button revealed
 *   on row hover. Click the value or pencil to enter edit mode.
 * Edit mode: input + save/cancel buttons. Enter saves; Esc cancels.
 *
 * Each row owns its own draft state and its own update mutation — failures
 * surface inline; row-version conflicts surface globally via the refresh
 * banner (C2), same convention as DefaultInspectionField above.
 */
type EditableRequiredTextFieldKey = 'projectTitle' | 'projectNumber';

type EditableTextFieldKey =
  | 'origin'
  | 'indentNumber'
  | 'makerNameBrand'
  | 'consigneeLocation'
  | 'inspectionAuthority'
  | 'partialDeliveryPaymentRemarks'
  | 'ndcReferenceNumber'
  | 'ndcRemarks'
  | 'otherCurrencyCode'
  | 'principalName'
  | 'principalAddress'
  | 'principalContactPerson'
  | 'principalEmail'
  | 'principalPhone'
  | 'principalFax'
  | 'portOfShipment'
  | 'principalBankName'
  | 'principalBankAccountNumber'
  | 'principalBankIban'
  | 'principalBankAccountTitle'
  | 'principalBankSwiftCode'
  | 'principalBankAddress'
  | 'principalBankEmail'
  | 'principalBankPhone'
  | 'principalBankFax';

type EditableDateFieldKey =
  | 'dfaReceivedDate'
  | 'dfaSubmittedDate'
  | 'contractSignDate'
  | 'dateOfIssuance'
  | 'technicalBidOpeningDate'
  | 'financialBidOpeningDate'
  | 'indentDate'
  | 'earnestSubmittedDate'
  | 'earnestReleasedDate'
  | 'ndcIssuedDate';

type EditableMoneyFieldKey = 'totalStoreValuePaisa' | 'requiredBgAmountPaisa' | 'earnestMoneyAmountPaisa';

type EditablePercentFieldKey = 'gstRateBps' | 'paymentOnINoteBps' | 'paymentOnCrvBps';

type EditableBooleanFieldKey = 'partialDeliveryAllowed' | 'partialPaymentAllowed' | 'ndcRequired';

type EditableFieldKey =
  | 'deliveryDurationMonths'
  | 'gracePeriodDays'
  | 'warrantyPeriodMonths'
  | 'bgSubmissionDurationDays'
  | 'bgValidityDurationMonths'
  | EditableRequiredTextFieldKey
  | EditableTextFieldKey
  | EditableDateFieldKey
  | EditableMoneyFieldKey
  | EditablePercentFieldKey
  | EditableBooleanFieldKey
  | 'termsOfDelivery'
  | 'projectCurrency'
  | 'clientId'
  | 'remarks';

interface EditableShellProps {
  project: Project;
  locked: boolean;
  label: string;
  field: EditableFieldKey;
  display: React.ReactNode;
  /** Children render in edit mode, between the row label and the save/cancel buttons. */
  renderEditor: (helpers: { save: () => void; cancel: () => void }) => React.ReactNode;
  /** Whether the editor is in a state that allows submission (validation). */
  canSave: boolean;
  /** The actual save payload (mutation body, excluding rowVersion). */
  buildPayload: () => Partial<Project>;
  onEnterEdit: () => void;
}

function EditableShell({
  project,
  locked,
  label,
  field,
  display,
  renderEditor,
  canSave,
  buildPayload,
  onEnterEdit,
}: EditableShellProps) {
  const [editing, setEditing] = useState(false);
  const update = useUpdateProject(project.id);
  const gpDrafts = useDraftsList({ draftType: 'GENERAL_PARTICULARS', projectId: project.id }, !locked);
  const saveDraft = useSaveDraft();
  const discardDraft = useDiscardDraft();

  const latestDraft = gpDrafts.data?.items[0] ?? null;
  const latestDraftPayload = latestDraft?.payloadJson ?? null;
  const draftField =
    latestDraftPayload && typeof latestDraftPayload.field === 'string'
      ? latestDraftPayload.field
      : null;
  const draftPayload =
    latestDraftPayload &&
    typeof latestDraftPayload.payload === 'object' &&
    latestDraftPayload.payload !== null &&
    !Array.isArray(latestDraftPayload.payload)
      ? (latestDraftPayload.payload as Partial<Project>)
      : null;
  const hasDraftForThisField = draftField === field && draftPayload !== null;

  const save = (): void => {
    if (!canSave) return;
    update.mutate(
      { ...buildPayload(), rowVersion: project.rowVersion },
      {
        onSuccess: () => setEditing(false),
      },
    );
  };

  const saveFieldDraft = (): void => {
    if (!canSave) return;
    saveDraft.mutate(
      {
        draftType: 'GENERAL_PARTICULARS',
        projectId: project.id,
        payloadJson: {
          field,
          label,
          payload: buildPayload() as unknown as Record<string, unknown>,
          savedProjectRowVersion: project.rowVersion,
        },
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  const submitDraft = (): void => {
    if (!latestDraft || !draftPayload) return;
    update.mutate(
      { ...draftPayload, rowVersion: project.rowVersion },
      {
        onSuccess: () => {
          discardDraft.mutate({ id: latestDraft.id, rowVersion: latestDraft.rowVersion });
        },
      },
    );
  };

  const discardFieldDraft = (): void => {
    if (!latestDraft) return;
    discardDraft.mutate({ id: latestDraft.id, rowVersion: latestDraft.rowVersion });
  };

  const cancel = (): void => {
    setEditing(false);
    update.reset();
    saveDraft.reset();
    discardDraft.reset();
  };

  if (!editing) {
    return (
      <div className="group">
        <dt className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">{label}</dt>
        <dd className="mt-0.5 flex flex-wrap items-center gap-1.5 text-sm text-primary tabular-nums">
          <span className="min-w-0 truncate">{display}</span>
          {!locked ? (
            <button
              type="button"
              aria-label={`Edit ${label.toLowerCase()}`}
              onClick={() => {
                onEnterEdit();
                setEditing(true);
              }}
              className="rounded-sm p-0.5 text-tertiary opacity-0 transition-opacity hover:bg-subtle hover:text-secondary group-hover:opacity-100 focus-visible:opacity-100"
            >
              <Pencil className="h-3 w-3" strokeWidth={1.5} />
            </button>
          ) : null}
          {hasDraftForThisField && !locked ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning-fg">
              Draft
              <button
                type="button"
                onClick={submitDraft}
                disabled={update.isPending || discardDraft.isPending}
                className="underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                Submit
              </button>
              <button
                type="button"
                onClick={discardFieldDraft}
                disabled={discardDraft.isPending}
                className="underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                Discard
              </button>
            </span>
          ) : null}
        </dd>
        {update.error && !update.error.isRowVersionConflict() ? (
          <p className="mt-1 text-xs text-danger-fg">{update.error.message}</p>
        ) : null}
        {discardDraft.error ? (
          <p className="mt-1 text-xs text-danger-fg">{discardDraft.error.message}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">{label}</dt>
      <dd className="mt-0.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {renderEditor({ save, cancel })}
          <button
            type="button"
            aria-label="Save"
            onClick={save}
            disabled={!canSave || update.isPending}
            className="rounded-sm p-1 text-success hover:bg-success/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {update.isPending ? (
              <Spinner className="h-3 w-3" />
            ) : (
              <Check className="h-3.5 w-3.5" strokeWidth={2} />
            )}
          </button>
          <button
            type="button"
            aria-label="Save draft"
            onClick={saveFieldDraft}
            disabled={!canSave || saveDraft.isPending}
            className="rounded-sm px-1.5 py-1 text-[11px] font-medium text-secondary hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saveDraft.isPending ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            aria-label="Cancel"
            onClick={cancel}
            disabled={update.isPending}
            className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-secondary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </div>
        {update.error && !update.error.isRowVersionConflict() ? (
          <p className="mt-1 text-xs text-danger-fg">{update.error.message}</p>
        ) : null}
        {saveDraft.error ? (
          <p className="mt-1 text-xs text-danger-fg">{saveDraft.error.message}</p>
        ) : null}
      </dd>
    </div>
  );
}

// ----- Integer field (with optional nullable) -----

function EditableIntegerField({
  project,
  locked,
  label,
  field,
  value,
  unit,
  min,
  max,
  nullable = false,
}: {
  project: Project;
  locked: boolean;
  label: string;
  field:
    | 'deliveryDurationMonths'
    | 'gracePeriodDays'
    | 'warrantyPeriodMonths'
    | 'bgSubmissionDurationDays'
    | 'bgValidityDurationMonths';
  value: number | null;
  unit: string;
  min: number;
  max: number;
  nullable?: boolean;
}) {
  // Empty string in the input maps to null (if nullable) or to "invalid".
  const [draft, setDraft] = useState<string>(value === null ? '' : String(value));
  const parsed: number | null = (() => {
    if (draft === '') return nullable ? null : NaN;
    const n = Number(draft);
    return Number.isInteger(n) && n >= min && n <= max ? n : NaN;
  })();
  const valid = parsed !== null ? Number.isFinite(parsed) : nullable;
  // Singular/plural toggle for "day" / "days" — months stay "months" either
  // way (idiomatically nobody says "1 month period" inline-edited here).
  const unitDisplay = unit === 'days' && value === 1 ? 'day' : unit;
  const display = value === null ? '—' : `${value} ${unitDisplay}`;
  return (
    <EditableShell
      project={project}
      locked={locked}
      label={label}
      field={field}
      display={display}
      onEnterEdit={() => setDraft(value === null ? '' : String(value))}
      canSave={valid && parsed !== value}
      buildPayload={() => ({ [field]: parsed } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={draft}
            autoFocus
            min={min}
            max={max}
            step={1}
            placeholder={nullable ? '—' : '0'}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') cancel();
            }}
            invalid={!valid}
            className="h-7 max-w-[80px] text-sm"
          />
          <span className="text-xs text-tertiary">{unit}</span>
        </div>
      )}
    />
  );
}

// ----- Nullable text field -----

function EditableTextField({
  project,
  locked,
  label,
  field,
  value,
  placeholder,
  uppercaseDisplay = false,
}: {
  project: Project;
  locked: boolean;
  label: string;
  field: EditableTextFieldKey;
  value: string | null;
  placeholder?: string;
  uppercaseDisplay?: boolean;
}) {
  const [draft, setDraft] = useState<string>(value ?? '');
  const trimmed = draft.trim();
  const normalised = trimmed === '' ? null : trimmed;
  return (
    <EditableShell
      project={project}
      locked={locked}
      label={label}
      field={field}
      display={uppercaseDisplay ? formatGeneralText(value) : (value ?? '—')}
      onEnterEdit={() => setDraft(value ?? '')}
      canSave={normalised !== value}
      buildPayload={() => ({ [field]: normalised } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <Input
          type="text"
          value={draft}
          autoFocus
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          className="h-7 max-w-[240px] text-sm"
        />
      )}
    />
  );
}


// ----- Required text, date, money, and percent fields -----

function EditableRequiredTextField({
  project,
  locked,
  label,
  field,
  value,
  uppercaseDisplay = false,
  maxWidth = 'max-w-[240px]',
}: {
  project: Project;
  locked: boolean;
  label: string;
  field: EditableRequiredTextFieldKey;
  value: string;
  uppercaseDisplay?: boolean;
  maxWidth?: string;
}) {
  const [draft, setDraft] = useState(value);
  const normalised = draft.trim();
  return (
    <EditableShell
      project={project}
      locked={locked}
      label={label}
      field={field}
      display={uppercaseDisplay ? formatGeneralText(value) : value}
      onEnterEdit={() => setDraft(value)}
      canSave={normalised.length > 0 && normalised !== value}
      buildPayload={() => ({ [field]: normalised } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <Input
          type="text"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          invalid={normalised.length === 0}
          className={`h-7 ${maxWidth} text-sm`}
        />
      )}
    />
  );
}

function EditableDateField({
  project,
  locked,
  label,
  field,
  value,
  nullable = true,
  max,
}: {
  project: Project;
  locked: boolean;
  label: string;
  field: EditableDateFieldKey;
  value: string | null;
  nullable?: boolean;
  max?: string;
}) {
  const [draft, setDraft] = useState(value ?? '');
  const normalised = draft.trim() === '' ? null : draft.trim();
  const valid = nullable ? true : normalised !== null;
  return (
    <EditableShell
      project={project}
      locked={locked}
      label={label}
      field={field}
      display={formatDate(value)}
      onEnterEdit={() => setDraft(value ?? '')}
      canSave={valid && normalised !== value}
      buildPayload={() => ({ [field]: normalised } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <DmyDateInput
          value={draft}
          autoFocus
          maxDate={max}
          onChange={setDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          invalid={!valid}
          className="h-7 max-w-[160px] text-sm"
        />
      )}
    />
  );
}

function moneyInputFromPaisa(value: number | null): string {
  if (value === null) return '';
  const rupees = value / 100;
  return Number.isInteger(rupees) ? String(rupees) : rupees.toFixed(2).replace(/\.00$/, '');
}

function bpsInputFromValue(value: number | null): string {
  if (value === null) return '';
  const percent = value / 100;
  return Number.isInteger(percent) ? String(percent) : percent.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function EditableMoneyField({
  project,
  locked,
  label,
  field,
  value,
}: {
  project: Project;
  locked: boolean;
  label: string;
  field: EditableMoneyFieldKey;
  value: number | null;
}) {
  const [draft, setDraft] = useState(moneyInputFromPaisa(value));
  const parsed = draft.trim() === '' ? null : parseRupeeInputToPaisa(draft);
  const valid = draft.trim() === '' || parsed !== null;
  return (
    <EditableShell
      project={project}
      locked={locked}
      label={label}
      field={field}
      display={formatOptionalCurrency(value)}
      onEnterEdit={() => setDraft(moneyInputFromPaisa(value))}
      canSave={valid && parsed !== value}
      buildPayload={() => ({ [field]: parsed } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <Input
          type="text"
          inputMode="decimal"
          value={draft}
          autoFocus
          placeholder="0"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          invalid={!valid}
          className="h-7 max-w-[150px] text-sm"
        />
      )}
    />
  );
}

function EditablePercentField({
  project,
  locked,
  label,
  field,
  value,
}: {
  project: Project;
  locked: boolean;
  label: string;
  field: EditablePercentFieldKey;
  value: number | null;
}) {
  const [draft, setDraft] = useState(bpsInputFromValue(value));
  const parsed = draft.trim() === '' ? null : parsePercentInputToBps(draft);
  const valid = draft.trim() === '' || parsed !== null;
  return (
    <EditableShell
      project={project}
      locked={locked}
      label={label}
      field={field}
      display={formatBpsAsPercent(value)}
      onEnterEdit={() => setDraft(bpsInputFromValue(value))}
      canSave={valid && parsed !== value}
      buildPayload={() => ({ [field]: parsed } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <div className="flex items-center gap-1">
          <Input
            type="text"
            inputMode="decimal"
            value={draft}
            autoFocus
            placeholder="0"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') cancel();
            }}
            invalid={!valid}
            className="h-7 max-w-[90px] text-sm"
          />
          <span className="text-xs text-tertiary">%</span>
        </div>
      )}
    />
  );
}


function EditablePaymentSplitField({ project, locked }: { project: Project; locked: boolean }) {
  const [iNoteDraft, setINoteDraft] = useState(bpsInputFromValue(project.paymentOnINoteBps));
  const [crvDraft, setCrvDraft] = useState(bpsInputFromValue(project.paymentOnCrvBps));
  const iNote = iNoteDraft.trim() === '' ? null : parsePercentInputToBps(iNoteDraft);
  const crv = crvDraft.trim() === '' ? null : parsePercentInputToBps(crvDraft);
  const bothBlank = iNoteDraft.trim() === '' && crvDraft.trim() === '';
  const valid = bothBlank || (iNote !== null && crv !== null && iNote + crv === 10_000);
  const changed = iNote !== project.paymentOnINoteBps || crv !== project.paymentOnCrvBps;
  const display =
    project.paymentOnINoteBps === null && project.paymentOnCrvBps === null
      ? '—'
      : `${formatBpsAsPercent(project.paymentOnINoteBps)} / ${formatBpsAsPercent(project.paymentOnCrvBps)}`;

  return (
    <EditableShell
      project={project}
      locked={locked}
      label="Payment split I-Note / CRV"
      field="paymentOnINoteBps"
      display={display}
      onEnterEdit={() => {
        setINoteDraft(bpsInputFromValue(project.paymentOnINoteBps));
        setCrvDraft(bpsInputFromValue(project.paymentOnCrvBps));
      }}
      canSave={valid && changed}
      buildPayload={() => ({
        paymentOnINoteBps: bothBlank ? null : iNote,
        paymentOnCrvBps: bothBlank ? null : crv,
      } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <div className="flex items-center gap-1">
          <Input
            type="text"
            inputMode="decimal"
            value={iNoteDraft}
            autoFocus
            placeholder="I-Note"
            onChange={(e) => setINoteDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') cancel();
            }}
            invalid={!valid}
            className="h-7 max-w-[72px] text-sm"
          />
          <span className="text-xs text-tertiary">/</span>
          <Input
            type="text"
            inputMode="decimal"
            value={crvDraft}
            placeholder="CRV"
            onChange={(e) => setCrvDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') cancel();
            }}
            invalid={!valid}
            className="h-7 max-w-[72px] text-sm"
          />
          <span className="text-xs text-tertiary">%</span>
        </div>
      )}
    />
  );
}


function EditableClientField({ project, locked }: { project: Project; locked: boolean }) {
  const clients = useClientsList();
  const options = clients.data ?? [];
  const [draft, setDraft] = useState(project.clientId);
  const current = options.find((client) => client.id === project.clientId);
  return (
    <EditableShell
      project={project}
      locked={locked}
      label="Client"
      field="clientId"
      display={current ? formatGeneralText(current.name) : project.clientId}
      onEnterEdit={() => setDraft(project.clientId)}
      canSave={draft !== project.clientId && draft.length > 0}
      buildPayload={() => ({ clientId: draft } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <Select
          value={draft}
          autoFocus
          disabled={clients.isLoading}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          className="h-7 max-w-[240px] text-sm"
        >
          {options.map((client) => (
            <option key={client.id} value={client.id} disabled={!client.isActive && client.id !== project.clientId}>
              {client.name}
            </option>
          ))}
        </Select>
      )}
    />
  );
}


// ----- Select and boolean fields -----

function ProjectCurrencyField({ project, locked }: { project: Project; locked: boolean }) {
  const [draft, setDraft] = useState<ProjectCurrencyCode>(project.projectCurrency);
  return (
    <EditableShell
      project={project}
      locked={locked}
      label="Project currency"
      field="projectCurrency"
      display={formatProjectCurrency(project)}
      onEnterEdit={() => setDraft(project.projectCurrency)}
      canSave={draft !== project.projectCurrency}
      buildPayload={() => ({
        projectCurrency: draft,
        ...(draft === 'OTHER'
          ? { otherCurrencyCode: project.otherCurrencyCode ?? 'OTHER' }
          : { otherCurrencyCode: null }),
      } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <Select
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value as ProjectCurrencyCode)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          className="h-7 max-w-[180px] text-sm"
        >
          <option value="PKR">PKR</option>
          <option value="USD">USD</option>
          <option value="OTHER">Other foreign currency</option>
        </Select>
      )}
    />
  );
}

function TermsOfDeliveryField({ project, locked }: { project: Project; locked: boolean }) {
  const incoterms = useIncotermsListAll();
  const [draft, setDraft] = useState<'' | ProjectTermsOfDelivery>(project.termsOfDelivery ?? '');
  const incotermOptions = useMemo(() => {
    const codes = incoterms.data?.length
      ? incoterms.data.filter((term) => term.isActive).map((term) => term.code)
      : [...TermsOfDelivery];
    if (project.termsOfDelivery && !codes.includes(project.termsOfDelivery)) {
      codes.push(project.termsOfDelivery);
    }
    return codes.filter((code, index) => codes.indexOf(code) === index).filter((code) => {
      return project.projectCurrency === 'PKR' || code !== 'FOR';
    });
  }, [incoterms.data, project.projectCurrency, project.termsOfDelivery]);
  const valid = !(project.projectCurrency !== 'PKR' && draft === 'FOR');
  return (
    <EditableShell
      project={project}
      locked={locked}
      label="Terms of delivery"
      field="termsOfDelivery"
      display={formatGeneralText(project.termsOfDelivery)}
      onEnterEdit={() => setDraft(project.termsOfDelivery ?? '')}
      canSave={valid && draft !== (project.termsOfDelivery ?? '')}
      buildPayload={() => ({ termsOfDelivery: draft === '' ? null : draft } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <Select
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value as '' | ProjectTermsOfDelivery)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          className="h-7 max-w-[180px] text-sm"
        >
          <option value="">—</option>
          {incotermOptions.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </Select>
      )}
    />
  );
}

function EditableBooleanField({
  project,
  locked,
  label,
  field,
  value,
}: {
  project: Project;
  locked: boolean;
  label: string;
  field: EditableBooleanFieldKey;
  value: boolean;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <EditableShell
      project={project}
      locked={locked}
      label={label}
      field={field}
      display={formatYesNo(value)}
      onEnterEdit={() => setDraft(value)}
      canSave={draft !== value}
      buildPayload={() => ({ [field]: draft } as Partial<Project>)}
      renderEditor={({ save, cancel }) => (
        <Select
          value={draft ? 'YES' : 'NO'}
          autoFocus
          onChange={(e) => setDraft(e.target.value === 'YES')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          className="h-7 max-w-[100px] text-sm"
        >
          <option value="YES">YES</option>
          <option value="NO">NO</option>
        </Select>
      )}
    />
  );
}

function formatProjectCurrency(project: Project): string {
  if (project.projectCurrency === 'OTHER') {
    return project.otherCurrencyCode ? formatGeneralText(project.otherCurrencyCode) : 'OTHER';
  }
  return project.projectCurrency;
}

function formatYesNo(value: boolean): string {
  return value ? 'YES' : 'NO';
}

// ----- Remarks (Textarea, outside the dl grid, gets its own block) -----

function EditableRemarks({ project, locked }: { project: Project; locked: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(project.remarks ?? '');
  const update = useUpdateProject(project.id);

  // Keep draft in sync if the project remarks change externally (e.g. another
  // user updated it) while we're not editing.
  useEffect(() => {
    if (!editing) setDraft(project.remarks ?? '');
  }, [project.remarks, editing]);

  const trimmed = draft.trim();
  const normalised = trimmed === '' ? null : trimmed;
  const canSave = normalised !== project.remarks;

  const save = (): void => {
    if (!canSave) return;
    update.mutate(
      { remarks: normalised, rowVersion: project.rowVersion },
      { onSuccess: () => setEditing(false) },
    );
  };

  const cancel = (): void => {
    setEditing(false);
    setDraft(project.remarks ?? '');
    update.reset();
  };

  // Closed projects without remarks for Assistants: nothing to show.
  if (!editing && !project.remarks && locked) return null;

  return (
    <div className="mt-4 border-t border-border-default pt-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">
          Remarks
        </div>
        {!editing && !locked ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs text-tertiary hover:bg-subtle hover:text-secondary"
          >
            <Pencil className="h-3 w-3" strokeWidth={1.5} />
            {project.remarks ? 'Edit' : 'Add'}
          </button>
        ) : null}
      </div>
      {editing ? (
        <div className="mt-1.5">
          <Textarea
            value={draft}
            autoFocus
            rows={4}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter to save (Enter alone inserts a newline in a
              // multi-line field — common convention).
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save();
              if (e.key === 'Escape') cancel();
            }}
            className="w-full text-sm"
          />
          <div className="mt-1.5 flex items-center justify-end gap-2">
            <span className="mr-auto text-[11px] text-tertiary">
              ⌘+Enter to save · Esc to cancel
            </span>
            <Button variant="tertiary" type="button" onClick={cancel} disabled={update.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={save}
              disabled={!canSave || update.isPending}
            >
              {update.isPending ? <Spinner className="h-3 w-3" /> : 'Save'}
            </Button>
          </div>
          {update.error && !update.error.isRowVersionConflict() ? (
            <p className="mt-1 text-xs text-danger-fg">{update.error.message}</p>
          ) : null}
        </div>
      ) : project.remarks ? (
        <p className="mt-1 whitespace-pre-wrap text-sm text-secondary">{formatGeneralText(project.remarks)}</p>
      ) : (
        <p className="mt-1 text-sm text-tertiary italic">No remarks.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Default inspection location — inline editor (spec §5.4)
// ---------------------------------------------------------------------------

/**
 * Spec §5.4: "Default Inspection Location — OUR_PREMISES | CLIENT_PREMISES | THIRD_PARTY_PREMISES | TBD."
 * Pre-fills client-facing inspection events but overridable per event.
 * UAT display wording changes the user-facing label from "Our premises" to
 * "Firm’s premises" and adds third-party/TBD options for projects whose inspection
 * location is not known at entry time."
 *
 * The field has lived in the schema, API, and inspection-event drawer
 * pre-fill since v1; only the edit affordance was missing. C3 adds it
 * here as a three-pill toggle so the user can flip the project default
 * without leaving the General particulars tab. Per spec §13 both Admin and
 * Assistant can edit project metadata, so no role gate is needed beyond
 * the closed-project lock that's enforced upstream by the API.
 *
 * The buttons immediately PATCH on click (no separate save) — the
 * choice is binary and the existing useUpdateProject hook handles
 * row-version tracking + cache invalidation. If the PATCH fails (e.g.
 * row-version conflict), the global refresh banner from C2 surfaces it.
 */
function DefaultInspectionField({ project }: { project: Project }) {
  const update = useUpdateProject(project.id);
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  // Spec §9 — closed projects lock edits against Assistants only; Admin keeps access.
  const locked = isLockedForUser(project, isAdmin);
  const current = project.defaultInspectionLocation;

  const setTo = (value: ProjectInspectionLocation): void => {
    if (value === current) return;
    update.mutate({ defaultInspectionLocation: value, rowVersion: project.rowVersion });
  };

  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">
        Default inspection
      </dt>
      <dd className="mt-1">
        <div role="radiogroup" aria-label="Default inspection location" className="inline-flex rounded-md border border-border-default p-0.5">
          <InspectionPill
            label="Firm’s premises"
            active={current === 'OUR_PREMISES'}
            disabled={locked || update.isPending}
            onClick={() => setTo('OUR_PREMISES')}
            loading={update.isPending && update.variables?.defaultInspectionLocation === 'OUR_PREMISES'}
          />
          <InspectionPill
            label="Client premises"
            active={current === 'CLIENT_PREMISES'}
            disabled={locked || update.isPending}
            onClick={() => setTo('CLIENT_PREMISES')}
            loading={update.isPending && update.variables?.defaultInspectionLocation === 'CLIENT_PREMISES'}
          />
          <InspectionPill
            label="Third party premises"
            active={current === 'THIRD_PARTY_PREMISES'}
            disabled={locked || update.isPending}
            onClick={() => setTo('THIRD_PARTY_PREMISES')}
            loading={update.isPending && update.variables?.defaultInspectionLocation === 'THIRD_PARTY_PREMISES'}
          />
          <InspectionPill
            label="TBD"
            active={current === 'TBD'}
            disabled={locked || update.isPending}
            onClick={() => setTo('TBD')}
            loading={update.isPending && update.variables?.defaultInspectionLocation === 'TBD'}
          />
        </div>
        {update.error && !update.error.isRowVersionConflict() ? (
          // Row-version conflicts surface globally via the refresh banner (C2).
          // Other failures (network, permission) show inline here so the user
          // sees the cause near the action.
          <p className="mt-1 text-xs text-danger-fg">{update.error.message}</p>
        ) : null}
      </dd>
    </div>
  );
}

function InspectionPill({
  label,
  active,
  disabled,
  loading,
  onClick,
}: {
  label: string;
  active: boolean;
  disabled: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      disabled={disabled}
      className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
        active
          ? 'bg-accent text-accent-fg'
          : 'text-secondary hover:bg-subtle disabled:hover:bg-transparent'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      {loading ? <Spinner className="h-3 w-3" /> : label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Close / reopen
// ---------------------------------------------------------------------------

function CloseButton({ projectId, rowVersion }: { projectId: string; rowVersion: number }) {
  const close = useCloseProject(projectId);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  // Only fetch readiness when the modal is open (avoids a useless query on every page load)
  const readiness = useClosureReadiness(projectId, open);

  const canClose = readiness.data?.canClose ?? false;
  const blockers = readiness.data?.blockers ?? [];
  // Pass 26 — spec §19(g) treats incomplete closure as a warning, not a
  // hard block. We let the user proceed when they explicitly acknowledge
  // the open items by clicking the secondary "Close anyway" button.
  const hasBlockers = blockers.length > 0;

  function doClose(acknowledgeIncompleteClosure: boolean) {
    close.mutate(
      {
        closureNotes: notes || undefined,
        rowVersion,
        ...(acknowledgeIncompleteClosure ? { acknowledgeIncompleteClosure: true } : {}),
      },
      { onSuccess: () => setOpen(false) },
    );
  }

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setOpen(true)}
        data-close-project-button="true"
      >
        Close project
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Close project?"
        size="sm"
        description="Closing locks all writes. You can reopen later if needed."
        footer={
          <>
            <Button variant="tertiary" onClick={() => setOpen(false)} disabled={close.isPending}>
              Cancel
            </Button>
            {hasBlockers ? (
              // Spec §19(g): incomplete closure is a warning. Surfaced as a
              // destructive-looking button so the user knows they're going
              // against the readiness check.
              <Button
                variant="destructive"
                loading={close.isPending}
                disabled={readiness.isLoading}
                onClick={() => doClose(true)}
              >
                Close anyway
              </Button>
            ) : (
              <Button
                variant="primary"
                loading={close.isPending}
                disabled={!canClose || readiness.isLoading}
                onClick={() => doClose(false)}
              >
                Close project
              </Button>
            )}
          </>
        }
      >
        {close.error ? (
          <Alert tone="danger" className="mb-3">
            {close.error.message}
          </Alert>
        ) : null}

        {/* Preconditions checklist */}
        {readiness.isLoading ? (
          <div className="mb-3 text-sm text-tertiary">Checking closure readiness…</div>
        ) : hasBlockers ? (
          <div className="mb-3 rounded-sm border border-warning/40 bg-warning/10 px-3 py-2">
            <div className="text-sm font-medium text-warning-strong">
              Pipeline still has open items
            </div>
            <ul className="mt-1.5 space-y-1 text-xs text-secondary">
              {blockers.map((b, i) => (
                <li key={`${b.code}-${i}`} className="flex items-start gap-1.5">
                  <span aria-hidden className="mt-0.5 text-warning-strong">
                    •
                  </span>
                  <span>{b.message}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-tertiary">
              Closing now is allowed but unusual — the open items will be captured in the audit
              log alongside the closure.
            </p>
          </div>
        ) : (
          <div className="mb-3 rounded-sm border border-success/40 bg-success/10 px-3 py-2 text-sm text-success-strong">
            All closure preconditions met — pipeline is complete.
          </div>
        )}

        <label className="block text-[13px] font-medium text-primary">
          Closure notes (optional)
        </label>
        <Textarea
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          className="mt-1"
          placeholder="Short summary of how the project ended"
        />
      </Modal>
    </>
  );
}

function ReopenButton({ projectId, rowVersion }: { projectId: string; rowVersion: number }) {
  const reopen = useReopenProject(projectId);
  return (
    <Button
      variant="secondary"
      leftIcon={<RotateCcw className="h-4 w-4" />}
      loading={reopen.isPending}
      onClick={() => reopen.mutate({ rowVersion })}
    >
      Reopen
    </Button>
  );
}
